import { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Sidebar from '../components/Sidebar/Sidebar';
import { pageHidden, pageOfLocation } from '../components/Sidebar/menuConfig';
import Header from '../components/Header/Header';
import { useAuthStore } from '../store';
import { accessControlAPI } from '../services/api';
import useAdminBranding from '../hooks/useAdminBranding';
import './MainLayout.css';
import '../styles/sb-admin.css';

const COLLAPSE_KEY = 'sidebar-collapsed';
// The page builder is a full-screen tool: no padding and no footer around it.
const TOOL_ROUTE = /^\/landing-pages\/[^/]+\/edit/;
// Access Control is read again on each page change and when the window comes back to
// the front — at most this often.
const ACCESS_REFRESH_MS = 10 * 1000;

function readCollapsed() {
    try { return sessionStorage.getItem(COLLAPSE_KEY) === '1'; } catch (_) { return false; }
}

const MainLayout = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(readCollapsed);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const mainRef = useRef(null);
    const { pathname } = useLocation();
    const { admin, setRoleTemplatePerms, setAccess } = useAuthStore();
    const access = useAuthStore((state) => state.access);
    const brand = useAdminBranding();
    const adminKey = admin ? `${admin.id || admin._id}:${admin.role}` : '';

    // What the superadmin set in Access Control — this admin's role (or the custom role
    // given to them) and their own sidebar — loaded on sign-in, then again on each page
    // change and when the window comes back to the front, so a change shows without
    // signing out. Not for the superadmin — they have full access.
    const adminRef = useRef(admin);
    adminRef.current = admin;
    const loadedAt = useRef(0);
    const loadAccess = useCallback(() => {
        const me = adminRef.current;
        if (!me || me.role === 'superadmin') return;
        if (Date.now() - loadedAt.current < ACCESS_REFRESH_MS) return;
        loadedAt.current = Date.now();
        accessControlAPI.getMine()
            .then(res => {
                const mine = res.data?.data;
                if (!mine) return;
                if (mine.template) setRoleTemplatePerms(mine.template.modules || []);
                setAccess(mine);
            })
            .catch(() => {}); // silently ignore — fallback to legacy logic
    }, [setRoleTemplatePerms, setAccess]);

    useEffect(() => { loadedAt.current = 0; loadAccess(); }, [adminKey, loadAccess]);
    useEffect(() => { loadAccess(); }, [pathname, loadAccess]);
    useEffect(() => {
        const onFront = () => { if (document.visibilityState !== 'hidden') loadAccess(); };
        window.addEventListener('focus', onFront);
        document.addEventListener('visibilitychange', onFront);
        return () => {
            window.removeEventListener('focus', onFront);
            document.removeEventListener('visibilitychange', onFront);
        };
    }, [loadAccess]);

    // A page hidden from this admin's sidebar is closed to them by its address too.
    const page = admin && admin.role !== 'superadmin' ? pageOfLocation(pathname, admin.role) : null;
    const pageClosed = !!page && pageHidden(page, access);
    useEffect(() => {
        if (pageClosed) toast.info('That page is not available to your account.', { toastId: 'page-closed' });
    }, [pageClosed]);

    // The mobile drawer closes on every navigation.
    useEffect(() => { setSidebarOpen(false); }, [pathname]);

    const toggleSidebar = () => setSidebarOpen(prev => !prev);

    const toggleCollapsed = () => {
        setCollapsed(prev => {
            const next = !prev;
            try { sessionStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch (_) { /* storage unavailable */ }
            return next;
        });
    };

    const isTool = TOOL_ROUTE.test(pathname);

    const handleScroll = (e) => setShowScrollTop(e.currentTarget.scrollTop > 100);
    const scrollToTop = () => mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

    return (
        <div className={`layout-wrapper sb-admin${collapsed ? ' sidebar-collapsed' : ''}`}>
            <Sidebar
                isOpen={sidebarOpen}
                toggleSidebar={toggleSidebar}
                collapsed={collapsed}
                toggleCollapsed={toggleCollapsed}
            />
            <div className="content-wrapper">
                <Header toggleSidebar={toggleSidebar} />
                <main
                    ref={mainRef}
                    className={`main-content${isTool ? ' is-tool' : ''}`}
                    onScroll={handleScroll}
                >
                    <div className="main-outlet">
                        {pageClosed ? <Navigate to="/dashboard" replace /> : <Outlet />}
                    </div>
                    {!isTool && (
                        <footer className="sb-footer">
                            <span>Copyright &copy; {brand.name} {new Date().getFullYear()}</span>
                        </footer>
                    )}
                </main>
                <button
                    type="button"
                    className={`scroll-to-top${showScrollTop ? ' visible' : ''}`}
                    onClick={scrollToTop}
                    aria-label="Scroll to top"
                    title="Scroll to top"
                >
                    <i className="fas fa-angle-up"></i>
                </button>
            </div>
        </div>
    );
};

export default MainLayout;
